// QualityTimelineTransformer.jsx
// Transforms backend API response into the format expected by QualityPlanTimeline D3 visualization

/**
 * Helper: Calculate day number from timestamp
 */
export const calculateDayFromTimestamp = (timestamp, batchStartDate) => {
    if (!timestamp || !batchStartDate) return null;

    // Normalize both dates to LOCAL midnight for reliable day difference calculation
    const start = new Date(batchStartDate);
    const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();

    const current = new Date(timestamp > 10000000000 ? timestamp : timestamp * 1000);
    const currentDateOnly = new Date(current.getFullYear(), current.getMonth(), current.getDate()).getTime();

    const diffMs = currentDateOnly - startDateOnly;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;

    return diffDays;
};

/**
 * Helper: Determine status based on target, actual, and control
 */
export const calculateStatus = (targetMin, targetMax, actual, control) => {
    if (actual === null || actual === undefined) return "pending";
    if (targetMin === null || targetMin === undefined || control === null || control === undefined) return "ok";

    const effectiveTargetMax = (targetMax !== null && targetMax !== undefined) ? targetMax : targetMin;

    const minBound = targetMin - control;
    const maxBound = effectiveTargetMax + control;

    // Crossing = Red
    if (actual < minBound || actual > maxBound) return "alert";

    // Touching = Yellow (strict or within small margin)
    if (Math.abs(actual - minBound) <= 0.1 || Math.abs(actual - maxBound) <= 0.1) return "warning";

    return "ok";
};

/**
 * Helper: Find active MetrcRun for a given day
 */
const getMetrcRunForDay = (day, metrcPlans) => {
    if (!metrcPlans || metrcPlans.length === 0) return null;

    // Collect all runs from all plans
    const allRuns = [];
    metrcPlans.forEach(plan => {
        if (plan.runs && plan.runs.length > 0) {
            plan.runs.forEach(run => {
                allRuns.push({
                    ...run,
                    planType: plan.type,
                    planName: plan.name
                });
            });
        }
    });

    // Sort by start_day descending to find the most recent applicable run
    const sortedRuns = allRuns.sort((a, b) => b.start_day - a.start_day);
    const activeRun = sortedRuns.find(run => run.start_day <= day);

    return activeRun || null;
};

/**
 * Helper: Get expected values for a specific sensor type and day
 */
const getExpectedValues = (sensorType, day, metrcPlans) => {
    // 1. Find a "Room-Wide Default" time discovery
    // We look for ANY plan in this phase that has defined times for this day
    let roomDayTime = "08:00:00";
    let roomNightTime = "20:00:00";
    let foundRoomTimes = false;

    if (metrcPlans && metrcPlans.length > 0) {
        for (const plan of metrcPlans) {
            const sortedRuns = [...(plan.runs || [])].sort((a, b) => b.start_day - a.start_day);
            const activeRun = sortedRuns.find(run => run.start_day <= day);
            if (activeRun && activeRun.day_time && activeRun.night_time) {
                roomDayTime = activeRun.day_time;
                roomNightTime = activeRun.night_time;
                foundRoomTimes = true;
                break; // Take the first one found as the room baseline
            }
        }
    }

    if (!metrcPlans || metrcPlans.length === 0) {
        return {
            expected_day: null,
            expected_night: null,
            day_control: null,
            night_control: null,
            day_time: roomDayTime,
            night_time: roomNightTime
        };
    }

    // 2. Find the specific plan for THIS sensor type
    const matchingPlan = metrcPlans.find(plan =>
        plan.type && sensorType && plan.type.toLowerCase() === sensorType.toLowerCase()
    );

    if (!matchingPlan || !matchingPlan.runs || matchingPlan.runs.length === 0) {
        return {
            expected_day: null,
            expected_night: null,
            day_control: null,
            night_control: null,
            day_time: roomDayTime,
            night_time: roomNightTime
        };
    }

    // Find the active run for this sensor on this day
    const sortedRuns = [...matchingPlan.runs].sort((a, b) => b.start_day - a.start_day);
    const activeRun = sortedRuns.find(run => run.start_day <= day);

    if (!activeRun) {
        return {
            expected_day: null,
            expected_night: null,
            day_control: null,
            night_control: null,
            day_time: roomDayTime,
            night_time: roomNightTime
        };
    }

    // 3. Prioritize: Sensor-Specific Time > Room-Wide Default > Fallback
    const finalDayTime = activeRun.day_time || roomDayTime;
    const finalNightTime = activeRun.night_time || roomNightTime;

    return {
        expected_day: activeRun.day_target,
        expected_night: activeRun.night_target,
        day_control: (activeRun.day_control !== undefined && activeRun.day_control !== null) ? activeRun.day_control : activeRun.control,
        night_control: (activeRun.night_control !== undefined && activeRun.night_control !== null) ? activeRun.night_control : activeRun.control,
        day_time: finalDayTime,
        night_time: finalNightTime
    };
};

/**
 * Main Transformer: Convert backend API response to D3 timeline format
 */
export const transformQualityTimelineData = (apiData, totalDays = 100) => {
    if (!apiData || !apiData.batch_details) {
        console.warn("Invalid API data structure");
        return {};
    }

    const { batch_details, sensors_data_actual = [], sensors_data_expected = [] } = apiData;
    const result = {};
    const batchStartDate = batch_details.actual_start_date || batch_details.target_subphase_date;
    const batchFrtl = batch_details.frtl || "1.1.1.1";
    const subphases = batch_details.subphases_details || [];
    const targetSubphaseId = batch_details.target_subphase_id;

    // Find the start day of the target subphase to anchor the timeline
    const targetPhase = subphases.find(sp => sp.default_subphase_id == targetSubphaseId || sp.id == targetSubphaseId);
    const targetStartDay = targetPhase ? targetPhase.start_day : 1;

    const uniqueBatchFrtls = (batch_details.unique_frtls || [batchFrtl]).filter(Boolean);

    // Create a map for room names and units based on sensor data
    const frtlToRoomMap = {};
    const sensorTypeToUnitMap = {};
    sensors_data_actual.forEach(m => {
        if (m.frtl && m.room_name) {
            frtlToRoomMap[m.frtl] = m.room_name;
        }
        if (m.sensor_type && m.unit) {
            const lowType = m.sensor_type.toLowerCase();
            if (!sensorTypeToUnitMap[lowType]) sensorTypeToUnitMap[lowType] = m.unit;
        }
    });

    // 2. Map actual data by day offset -> frtl -> sensor type (case-insensitive type)
    const actualDataMap = {}; // { [dayOffset]: { [frtl]: { [sensorTypeLow]: points[] } } }

    sensors_data_actual.forEach(metric => {
        const sensorTypeLow = metric.sensor_type ? metric.sensor_type.toLowerCase() : "unknown";
        const metricFrtl = metric.frtl;
        const metricRoomId = metric.room_id;

        if (!metric.data_points) return;

        metric.data_points.forEach(point => {
            const timestamp = point.start_timestamp || point.timestamp;
            const dayOffset = calculateDayFromTimestamp(timestamp, batchStartDate);

            // dayOffset is 1 if it matches batchStartDate
            if (dayOffset !== null && dayOffset >= 1 && dayOffset <= totalDays) {
                if (!actualDataMap[dayOffset]) actualDataMap[dayOffset] = {};

                const targetFrtls = [];
                if (metricFrtl) {
                    if (uniqueBatchFrtls.includes(metricFrtl)) {
                        targetFrtls.push(metricFrtl);
                    } else {
                        // Match by Room.Table prefix (e.g., 1.1.2.2 matches 1.1.18.18)
                        const sParts = metricFrtl.split('.');
                        if (sParts.length >= 2) {
                            const sPrefix = `${sParts[0]}.${sParts[1]}.`;
                            uniqueBatchFrtls.forEach(bf => {
                                if (typeof bf === 'string' && bf.startsWith(sPrefix)) {
                                    targetFrtls.push(bf);
                                }
                            });
                        }
                    }
                }

                // Fallback to room-wide matching if still not mapped
                if (targetFrtls.length === 0 && metricRoomId) {
                    uniqueBatchFrtls.forEach(bf => {
                        if (typeof bf === 'string') {
                            const parts = bf.split('.');
                            if (parts.length >= 2 && parseInt(parts[1]) === metricRoomId) {
                                targetFrtls.push(bf);
                            }
                        }
                    });
                }

                targetFrtls.forEach(f => {
                    if (!actualDataMap[dayOffset][f]) actualDataMap[dayOffset][f] = {};
                    if (!actualDataMap[dayOffset][f][sensorTypeLow]) actualDataMap[dayOffset][f][sensorTypeLow] = [];
                    actualDataMap[dayOffset][f][sensorTypeLow].push(point);
                });
            }
        });
    });

    // 3. Build the timeline day by day (relative to anchor)
    for (let day = 1; day <= totalDays; day++) {
        // Absolute day for getExpectedValues
        const absoluteDay = day + (targetStartDay - 1);
        const metrcRun = getMetrcRunForDay(absoluteDay, sensors_data_expected);
        const frtlEntries = [];

        uniqueBatchFrtls.forEach(frtl => {
            const readings = [];

            sensors_data_expected.forEach(plan => {
                const planTypeOrig = plan.type || "unknown";
                const planTypeLow = planTypeOrig.toLowerCase();
                const expected = getExpectedValues(planTypeLow, absoluteDay, sensors_data_expected);
                const dayPoints = (actualDataMap[day] && actualDataMap[day][frtl]) ? actualDataMap[day][frtl][planTypeLow] : [];

                let actualVal = null;
                let actualHigh = null;
                let actualLow = null;
                let status = "pending";
                let hourlyData = [];

                const expDayVal = (expected.expected_day !== null && expected.expected_day !== undefined) ? parseFloat(expected.expected_day) : null;
                const expNightVal = (expected.expected_night !== null && expected.expected_night !== undefined) ? parseFloat(expected.expected_night) : expDayVal;

                const dayControlVal = (expected.day_control !== null && expected.day_control !== undefined) ? parseFloat(expected.day_control) : null;
                const nightControlVal = (expected.night_control !== null && expected.night_control !== undefined) ? parseFloat(expected.night_control) : dayControlVal;

                const dayStartHour = parseInt(expected.day_time.split(':')[0]);
                const nightStartHour = parseInt(expected.night_time.split(':')[0]);

                if (dayPoints && dayPoints.length > 0) {
                    let sum = 0;
                    let count = 0;
                    let hSum = 0; let hCount = 0;
                    let lSum = 0; let lCount = 0;

                    dayPoints.forEach(point => {
                        const rawVal = point.average_value || point.avg;
                        const rawHigh = point.high_value || point.high;
                        const rawLow = point.low_value || point.low;

                        const v = (rawVal !== null && rawVal !== undefined) ? parseFloat(rawVal) : NaN;
                        const h = (rawHigh !== null && rawHigh !== undefined) ? parseFloat(rawHigh) : NaN;
                        const l = (rawLow !== null && rawLow !== undefined) ? parseFloat(rawLow) : NaN;

                        if (!isNaN(v)) {
                            sum += v;
                            count++;
                        }
                        if (!isNaN(h)) {
                            hSum += h;
                            hCount++;
                        }
                        if (!isNaN(l)) {
                            lSum += l;
                            lCount++;
                        }
                    });

                    if (count > 0) {
                        actualVal = sum / count;
                        actualHigh = hCount > 0 ? (hSum / hCount) : null;
                        actualLow = lCount > 0 ? (lSum / lCount) : null;

                        // Daily status will be updated during hourly loop to be more precise
                        status = "ok";
                    }

                    const hours = {};
                    dayPoints.forEach(p => {
                        const ts = p.start_timestamp || p.timestamp;
                        const date = new Date(ts > 10000000000 ? ts : ts * 1000);
                        const hr = date.getHours(); // Use local hours for user context
                        if (!hours[hr]) hours[hr] = [];
                        hours[hr].push(p);
                    });

                    for (let i = 0; i < 24; i++) {
                        const pts = hours[i] || [];
                        let hrVal = null;
                        let hrHigh = null;
                        let hrLow = null;
                        let hrStatus = "ok";
                        if (pts.length > 0) {
                            // Determine if this hour is Day or Night
                            let currentTarget = expDayVal;
                            let currentControl = dayControlVal;

                            if (nightStartHour > dayStartHour) { // Night spans across midnight (e.g., 20:00 - 08:00)
                                if (i >= nightStartHour || i < dayStartHour) {
                                    currentTarget = expNightVal;
                                    currentControl = nightControlVal;
                                }
                            } else { // Night is within the same day (e.g., 08:00 - 20:00 day)
                                if (i >= nightStartHour && i < dayStartHour) {
                                    currentTarget = expNightVal;
                                    currentControl = nightControlVal;
                                }
                            }

                            // Average all points within this hour
                            let hrSum = 0;
                            let hrCount = 0;
                            let hrMax = null;
                            let hrMin = null;

                            pts.forEach(p => {
                                const raw = p.average_value || p.avg;
                                const h = p.high_value || p.high;
                                const l = p.low_value || p.low;

                                const v = (raw !== null && raw !== undefined) ? parseFloat(raw) : NaN;
                                if (!isNaN(v)) {
                                    hrSum += v;
                                    hrCount++;
                                }

                                if (h !== null && h !== undefined) hrMax = (hrMax === null) ? parseFloat(h) : Math.max(hrMax, parseFloat(h));
                                if (l !== null && l !== undefined) hrMin = (hrMin === null) ? parseFloat(l) : Math.min(hrMin, parseFloat(l));
                            });

                            hrVal = hrCount > 0 ? (hrSum / hrCount) : null;

                            // Calculate status using the PERIOD-SPECIFIC target and control
                            const statHigh = calculateStatus(currentTarget, null, hrMax || hrVal, currentControl);
                            const statLow = calculateStatus(currentTarget, null, hrMin || hrVal, currentControl);

                            if (statHigh === 'alert' || statLow === 'alert') hrStatus = 'alert';
                            else if (statHigh === 'warning' || statLow === 'warning') hrStatus = 'warning';

                            // Update daily status based on this hour
                            if (hrStatus === 'alert') status = 'alert';
                            else if (hrStatus === 'warning' && status !== 'alert') status = 'warning';

                            // Also set hrHigh and hrLow for the hourly data point
                            pts.forEach(p => {
                                const pHigh = p.high_value || p.high;
                                const pLow = p.low_value || p.low;
                                const hVal = (pHigh !== null && pHigh !== undefined) ? parseFloat(pHigh) : null;
                                const lVal = (pLow !== null && pLow !== undefined) ? parseFloat(pLow) : null;
                                if (hVal !== null) hrHigh = (hrHigh === null) ? hVal : Math.max(hrHigh, hVal);
                                if (lVal !== null) hrLow = (hrLow === null) ? lVal : Math.min(hrLow, lVal);
                            });
                        }
                        hourlyData.push({ hour: i, value: hrVal, high: hrHigh, low: hrLow, status: hrStatus });
                    }
                }

                readings.push({
                    sensor_type: planTypeOrig,
                    expected_day: expDayVal,
                    expected_night: expNightVal,
                    day_control: dayControlVal,
                    night_control: nightControlVal,
                    day_time: expected.day_time,
                    night_time: expected.night_time,
                    actual_day_avg: actualVal,
                    actual_high: actualHigh,
                    actual_low: actualLow,
                    actual_night_avg: null,
                    actual_original_avg: actualVal,
                    unit: sensorTypeToUnitMap[planTypeLow] || null,
                    status: status,
                    hourly_data: hourlyData
                });
            });

            // Get room name from map or parse from FRTL
            let roomName = frtlToRoomMap[frtl];
            if (!roomName && typeof frtl === 'string') {
                const parts = frtl.split(".");
                roomName = parts.length >= 2 ? `Room ${parts[1]}` : `Room ${batch_details.room_id || 'Unknown'}`;
            }

            frtlEntries.push({
                frtl: frtl,
                room_name: roomName || "Unknown Room",
                readings: readings
            });
        });

        result[day] = {
            metrc_run_name: metrcRun ? metrcRun.name : null,
            frtls: frtlEntries
        };
    }

    return result;
};
